import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ScanLine, Image as ImageIcon, Link2, Link2Off, Eye, Loader2,
    ChevronDown, ChevronUp, Building2, Calendar, FileText, RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODALITY_LABEL = {
    XR: 'X-ray', CR: 'X-ray', MR: 'MRI', CT: 'CT', US: 'Ultrasound', NM: 'Nuclear medicine',
};

// Normalise XCA-I (RAD-75) imaging documents into the panel's display shape.
const normalizeXcai = (docs) => (docs || []).map((d) => ({
    studyInstanceUID: d.studyInstanceUID,
    modality: d.modality,
    bodyPart: d.bodyPart,
    studyDate: d.studyDate,
    performingOrganisation: d.performingOrganisation,
    reportStatus: d.reportStatus,
    numberOfInstances: d.numberOfInstances,
}));

// Normalise MHD (ITI-67) FHIR DocumentReference entries into the same shape.
const normalizeMhd = (bundle) => ((bundle?.entry) || []).map((e) => {
    const r = e.resource || {};
    const modality = r.context?.event?.[0]?.coding?.[0]?.code || '';
    const uidRaw = r.context?.related?.[0]?.identifier?.value || '';
    const studyInstanceUID = uidRaw.replace('urn:oid:', '');
    const desc = (r.description || '').replace(/report/i, '').trim();
    const bodyPart = desc.replace(modality, '').trim() || desc;
    return {
        studyInstanceUID,
        modality,
        bodyPart,
        studyDate: (r.date || '').slice(0, 10),
        performingOrganisation: r.author?.[0]?.display || '',
        reportStatus: r.docStatus === 'final' ? 'final' : 'unreported',
        numberOfInstances: null,
    };
});

function StatusBadge({ status }) {
    if (status === 'final') {
        return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Reported</Badge>;
    }
    return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Unreported</Badge>;
}

/**
 * NIR Imaging Panel - surfaces a patient's prior imaging from the NHS National
 * Imaging Registry at the point of referral/triage.
 *  - Runs the IHE XCA-I (RAD-75) cross-gateway query for the patient.
 *  - "View" retrieves a rendered frame via WADO-RS (DICOMweb).
 *  - "Link" attaches the study UID to this referral (audit-logged).
 * Auth: axios.defaults Authorization is set globally by AuthContext.
 * NOTE: NIR runs in SANDBOX / mock mode until live credentials are provisioned.
 */
export default function NIRImagingPanel({ referralId, nhsNumber }) {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [linked, setLinked] = useState(new Set());
    const [linkingUid, setLinkingUid] = useState(null);
    const [viewing, setViewing] = useState(null); // { uid, label, blobUrl, loading }
    const [useMhd, setUseMhd] = useState(false);   // false = XCA-I (RAD-75), true = MHD (ITI-67)

    const loadLinked = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/nir/referral/${referralId}/studies`);
            const uids = (res.data?.linked_studies || []).map((s) => s.study_instance_uid);
            setLinked(new Set(uids));
        } catch (_) { /* non-fatal */ }
    }, [referralId]);

    const runQuery = useCallback(async () => {
        if (!nhsNumber) return;
        setLoading(true);
        setError(null);
        try {
            let next;
            if (useMhd) {
                const res = await axios.get(`${API}/nir/ihe/mhd/DocumentReference`, { params: { patient: nhsNumber } });
                next = normalizeMhd(res.data);
            } else {
                const res = await axios.get(`${API}/nir/ihe/xca-i/query`, { params: { patient: nhsNumber } });
                next = normalizeXcai(res.data?.imagingDocuments);
            }
            setItems(next);
            await loadLinked();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(detail || 'Unable to query NIR for imaging.');
        } finally {
            setLoading(false);
        }
    }, [nhsNumber, useMhd, loadLinked]);

    useEffect(() => { runQuery(); }, [runQuery]);

    const handleLink = async (uid) => {
        setLinkingUid(uid);
        try {
            await axios.post(`${API}/nir/referral/${referralId}/link`, null, { params: { study_instance_uid: uid } });
            setLinked((prev) => new Set(prev).add(uid));
            toast.success('Imaging study linked to referral');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not link study');
        } finally {
            setLinkingUid(null);
        }
    };

    const handleView = async (item) => {
        const label = `${MODALITY_LABEL[item.modality] || item.modality} ${item.bodyPart}`;
        setViewing({ uid: item.studyInstanceUID, label, blobUrl: null, loading: true });
        try {
            const res = await axios.get(
                `${API}/nir/ihe/wado-rs/studies/${encodeURIComponent(item.studyInstanceUID)}/rendered`,
                { responseType: 'blob' },
            );
            const blobUrl = URL.createObjectURL(res.data);
            setViewing({ uid: item.studyInstanceUID, label, blobUrl, loading: false });
        } catch (e) {
            toast.error('Could not retrieve image via WADO-RS');
            setViewing(null);
        }
    };

    const closeViewer = () => {
        if (viewing?.blobUrl) URL.revokeObjectURL(viewing.blobUrl);
        setViewing(null);
    };

    if (!nhsNumber) {
        return (
            <Card className="card-clinical border-dashed" data-testid="nir-imaging-panel-empty">
                <CardContent className="p-4 text-sm text-slate-500 flex items-center gap-2">
                    <ScanLine className="w-4 h-4" />
                    NIR imaging lookup unavailable — no NHS number on this referral.
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className="card-clinical" data-testid="nir-imaging-panel">
                <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-md bg-sky-600 text-white grid place-items-center">
                                <ScanLine className="w-4 h-4" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                    NIR imaging history
                                    <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">
                                        sandbox
                                    </Badge>
                                </div>
                                <p className="text-xs text-slate-500" data-testid="nir-method-label">
                                    via IHE {useMhd ? 'MHD (ITI-67)' : 'XCA-I (RAD-75)'} + WADO-RS
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="flex items-center gap-1.5 mr-1" data-testid="nir-method-toggle">
                                <span className={`text-[11px] font-medium ${!useMhd ? 'text-sky-700' : 'text-slate-400'}`}>XCA-I</span>
                                <Switch checked={useMhd} onCheckedChange={setUseMhd} data-testid="nir-method-switch" />
                                <span className={`text-[11px] font-medium ${useMhd ? 'text-sky-700' : 'text-slate-400'}`}>MHD</span>
                            </div>
                            <Button variant="ghost" size="sm" onClick={runQuery} disabled={loading} data-testid="nir-refresh-btn">
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)} data-testid="nir-panel-toggle">
                                {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>

                    {open && (
                        <div className="space-y-2">
                            {loading && (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Querying National Imaging Registry...
                                </div>
                            )}
                            {error && !loading && <p className="text-sm text-slate-500 italic">{error}</p>}
                            {!loading && !error && items.length === 0 && (
                                <p className="text-sm text-slate-500 italic" data-testid="nir-no-results">
                                    No prior imaging found in NIR for this patient.
                                </p>
                            )}
                            {!loading && items.map((item) => {
                                const isLinked = linked.has(item.studyInstanceUID);
                                return (
                                    <div
                                        key={item.studyInstanceUID}
                                        className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/60"
                                        data-testid="nir-study-row"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-semibold text-slate-800">
                                                    {MODALITY_LABEL[item.modality] || item.modality} · {item.bodyPart}
                                                </span>
                                                <StatusBadge status={item.reportStatus} />
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{item.studyDate}</span>
                                                <span className="flex items-center gap-1 truncate"><Building2 className="w-3 h-3 shrink-0" />{item.performingOrganisation}</span>
                                                <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{item.numberOfInstances} images</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Button
                                                variant="outline" size="sm"
                                                onClick={() => handleView(item)}
                                                data-testid="nir-view-btn"
                                                className="border-sky-300 text-sky-700 hover:bg-sky-100"
                                            >
                                                <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                                            </Button>
                                            <Button
                                                variant={isLinked ? 'secondary' : 'outline'}
                                                size="sm"
                                                disabled={isLinked || linkingUid === item.studyInstanceUID}
                                                onClick={() => handleLink(item.studyInstanceUID)}
                                                data-testid="nir-link-btn"
                                            >
                                                {linkingUid === item.studyInstanceUID ? (
                                                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                                ) : isLinked ? (
                                                    <Link2 className="w-3.5 h-3.5 mr-1.5" />
                                                ) : (
                                                    <Link2Off className="w-3.5 h-3.5 mr-1.5" />
                                                )}
                                                {isLinked ? 'Linked' : 'Link'}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                            {!loading && items.length > 0 && (
                                <p className="text-[11px] text-slate-400 italic pt-1">
                                    Metadata from NIR (sandbox). Images shown are mock WADO-RS renders; production views hand off to the holding Trust PACS/IEP.
                                </p>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) closeViewer(); }}>
                <DialogContent className="max-w-lg" data-testid="nir-image-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <ImageIcon className="w-4 h-4 text-sky-600" />
                            {viewing?.label || 'Imaging'} — WADO-RS
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-400">
                            Sandbox image retrieved via WADO-RS (DICOMweb) for the NIR Beta demo.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid place-items-center min-h-[280px] bg-slate-900 rounded-md">
                        {viewing?.loading ? (
                            <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
                        ) : viewing?.blobUrl ? (
                            <img src={viewing.blobUrl} alt={viewing.label} className="max-h-[420px] w-auto rounded" data-testid="nir-image-view" />
                        ) : null}
                    </div>
                    <p className="text-[11px] text-slate-400 italic">
                        Sandbox render for the NIR Beta demo. Study UID: {viewing?.uid}
                    </p>
                </DialogContent>
            </Dialog>
        </>
    );
}
